import { useCallback, useId, useRef, useState } from 'react';

interface SparklineProps {
  values: number[];
  /** Couleur CSS (token) du tracé et du dégradé. */
  color: string;
  className?: string;
  /**
   * Libellés alignés sur `values` (mois). Leur présence ACTIVE l'infobulle —
   * sans eux, une valeur survolée n'aurait pas de repère temporel.
   */
  labels?: string[];
  /** Formatage de la valeur affichée dans l'infobulle. */
  formatValue?: (value: number) => string;
  /** Intitulé de la série, en tête d'infobulle. */
  seriesLabel?: string;
}

const W = 100;
const H = 24;
const PAD = 3;

/**
 * Micro-courbe des KPI — aire dégradée + tracé + point terminal accentué.
 *
 * Dessinée à la main en SVG (pas de lib) : 3 nœuds pour un rendu net à 22 px de
 * haut, là où Recharts imposerait un conteneur responsive et un coût inutile.
 *
 * Si `labels` est fourni, la courbe devient interactive : le survol horizontal
 * désigne le point le plus proche et affiche sa valeur datée. L'infobulle est
 * placée SOUS la courbe — au-dessus, elle masquerait le chiffre du KPI, c'est-
 * à-dire précisément ce qu'on est en train de lire.
 */
export default function Sparkline({
  values,
  color,
  className,
  labels,
  formatValue,
  seriesLabel,
}: SparklineProps) {
  const gradientId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const indexFromPointer = useCallback(
    (clientX: number) => {
      const svg = svgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      if (!rect.width) return null;
      const ratio = (clientX - rect.left) / rect.width;
      const i = Math.round(ratio * (values.length - 1));
      return Math.max(0, Math.min(values.length - 1, i));
    },
    [values.length],
  );

  // Moins de 2 points : rien à tracer (une ligne a besoin de deux extrémités).
  if (!values || values.length < 2) return <svg className={className ?? 'spark'} aria-hidden />;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = PAD + (1 - (v - min) / range) * (H - 2 * PAD);
    return [x, y] as const;
  });

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const area = `${line} L${W} ${H} L0 ${H} Z`;
  const last = points[points.length - 1];
  const interactive = !!labels && labels.length === values.length;
  const activeIndex = interactive ? hover : null;

  const svg = (
    <svg
      ref={svgRef}
      className={className ?? 'spark'}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity=".22" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {activeIndex === null ? (
        <circle cx={last[0]} cy={last[1]} r="2.1" fill={color} />
      ) : (
        <>
          {/* preserveAspectRatio="none" écrase les formes : le guide est un
              rect fin (déformable) plutôt qu'une ligne, et le repère une barre
              plutôt qu'un cercle, qui deviendrait une ellipse. */}
          <rect
            x={points[activeIndex][0] - 0.35}
            y={0}
            width="0.7"
            height={H}
            fill="var(--line-2)"
          />
          <rect
            x={points[activeIndex][0] - 0.9}
            y={points[activeIndex][1] - 2.6}
            width="1.8"
            height="5.2"
            rx="0.9"
            fill={color}
          />
        </>
      )}
    </svg>
  );

  if (!interactive) return svg;

  return (
    <div className="relative">
      {svg}
      {activeIndex !== null && (
        <div
          className="ctip"
          style={{
            left: `${(points[activeIndex][0] / W) * 100}%`,
            top: '100%',
            transform: `translate(${
              activeIndex === 0 ? '0' : activeIndex === values.length - 1 ? '-100%' : '-50%'
            }, 6px)`,
          }}
          aria-hidden
        >
          <div className="r1">{labels![activeIndex]}</div>
          <div className="r2">
            {seriesLabel} <b className="num">{formatValue ? formatValue(values[activeIndex]) : values[activeIndex]}</b>
          </div>
        </div>
      )}
      <div
        className="absolute inset-0 cursor-crosshair"
        aria-hidden
        onPointerMove={(e) => setHover(indexFromPointer(e.clientX))}
        onPointerLeave={() => setHover(null)}
      />
    </div>
  );
}
